import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardBody,
  FormField,
  Input,
  ReadOnlyValue,
  Select,
} from '@/shared/components';
import type { WorkspaceSettingsResponse } from '@/shared/api';
import { REPLY_ADDRESS_HINT } from '@/shared/lib/replyAddress';
import { currencyOptions, timeZoneOptions } from '@/shared/lib/workspaceValueOptions';
import { useWorkspaceSettingsSave } from './useWorkspaceSettingsSave';
import {
  saveFailureMessage,
  serverFieldErrors,
  toFormValues,
  toRequest,
  workspaceSettingsSchema,
  type WorkspaceSettingsValues,
} from './workspaceSettingsModel';

export function WorkspaceSettingsForm({ settings }: { settings: WorkspaceSettingsResponse }) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty },
  } = useForm<WorkspaceSettingsValues>({
    resolver: zodResolver(workspaceSettingsSchema),
    defaultValues: toFormValues(settings),
  });
  const { save, saving, problem } = useWorkspaceSettingsSave();

  // Neuer Serverstand (gespeichert oder anderswo geändert) ersetzt die Eingaben.
  useEffect(() => reset(toFormValues(settings)), [settings, reset]);
  useEffect(() => {
    for (const [field, message] of serverFieldErrors(problem)) setError(field, { message });
  }, [problem, setError]);

  const currencyChanged = watch('defaultCurrency') !== (settings.defaultCurrency ?? '');

  return (
    <form onSubmit={handleSubmit((values) => save(toRequest(values)))} noValidate>
      <Card>
        <CardBody className="@container space-y-5 p-5 sm:p-6">
          <ReadOnlyValue
            label="Unternehmenskürzel"
            value={settings.slug}
            mono
            hint="Damit melden sich alle Benutzer an. Es lässt sich nicht ändern."
          />
          <FormField label="Name" error={errors.name?.message} required>
            <Input autoComplete="organization" {...register('name')} />
          </FormField>
          <div className="@xl:grid-cols-2 grid gap-5">
            <FormField
              label="Zeitzone"
              error={errors.timeZone?.message}
              required
              hint="Gilt für neue Objekte. Bestehende behalten ihre Zeitzone."
            >
              <Select {...register('timeZone')}>
                {!settings.timeZone && <option value="">Zeitzone wählen …</option>}
                {timeZoneOptions(settings.timeZone).map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Währung"
              error={errors.defaultCurrency?.message}
              required
              hint={
                currencyChanged
                  ? 'Bestehende Objekte behalten ihre Währung. Eine Stripe-Verbindung muss danach neu geprüft werden; bis dahin sind Zahlungen ausgesetzt.'
                  : 'Gilt für neue Objekte. Bestehende behalten ihre Währung.'
              }
            >
              <Select {...register('defaultCurrency')}>
                {!settings.defaultCurrency && <option value="">Währung wählen …</option>}
                {currencyOptions(settings.defaultCurrency).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
          <FormField
            label="Kontakt-E-Mail"
            error={errors.contactEmail?.message}
            hint={`${REPLY_ADDRESS_HINT} Leer lassen, wenn es keine gibt.`}
          >
            <Input type="email" autoComplete="email" {...register('contactEmail')} />
          </FormField>
          {problem && (
            <p role="alert" className="text-sealwax text-sm">
              {saveFailureMessage(problem)}
            </p>
          )}
        </CardBody>
      </Card>
      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          variant="brand"
          className="min-h-11"
          loading={saving}
          disabled={!isDirty || saving}
        >
          Speichern
        </Button>
      </div>
    </form>
  );
}
